import moment from 'moment-timezone';
import isNaN from 'lodash/isNaN';
import {
  END_OPTION_SPECIFIC_DATE,
  IS_RECURRING_NO,
} from '../../../edit-listing/common/config/sessions';
import { getCustomSession } from './getCustomSession';

/**
 * Shared helper function to calculate end booking date based on publicData
 * @param {Object} publicData - The listing's public data
 * @returns {moment.Moment|null} - The calculated end booking date as moment object or null
 */
const calculateEndBookingDate = (publicData) => {
  const {
    endOption,
    endDate,
    numberOfSessions,
    recurringOption,
    endTime,
    isRecurring,
    availabilityPlan,
    firstSession,
    firstDayOfWeek,
    blockBooking,
  } = publicData || {};

  if (isRecurring === IS_RECURRING_NO) return null;

  let endBookingDate = null;

  if (endOption === END_OPTION_SPECIFIC_DATE && endDate) {
    endBookingDate = moment(endDate);
  } else if (numberOfSessions && recurringOption) {
    const firstSession = publicData.firstSession ? moment(publicData.firstSession) : moment();
    if (recurringOption === 'every-day') {
      endBookingDate = firstSession.clone().add(numberOfSessions - 1, 'days');
    } else if (recurringOption === 'every-week') {
      endBookingDate = firstSession.clone().add((numberOfSessions - 1) * 7, 'days');
    } else if (recurringOption === 'every-weekday') {
      // For weekdays, we need to calculate properly accounting for weekends
      const current = firstSession.clone();
      // firstSession can fall on a weekend, which is not a real weekday session. Advance to the first
      // weekday before counting it as session 1 — otherwise the weekend anchor is miscounted and the
      // end date lands one session short (matching getNonCustomSession, which skips weekend anchors).
      while (current.isoWeekday() > 5) {
        current.add(1, 'day');
      }
      let sessionCount = 1; // Start with 1 since we're already at the first session

      while (sessionCount < numberOfSessions) {
        current.add(1, 'day');
        // Only count weekdays (Monday = 1, Friday = 5 in moment.js)
        if (current.isoWeekday() <= 5) {
          sessionCount++;
        }
      }
      endBookingDate = current;
    } else if (recurringOption === 'custom-schedule') {
      const sessions = getCustomSession({
        firstSession,
        numberOfSessions,
        endDate,
        endOption,
        availabilityPlan,
        firstDayOfWeek,
        blockBooking,
      });

      if (Array.isArray(sessions) && sessions.length > 0) {
        endBookingDate = sessions[sessions.length - 1].rawDate.clone();
      }
    }
  }

  if (!endBookingDate) return null;

  // Add the session end time (HH:mm format)
  if (endTime) {
    const parts = endTime.split(':');
    if (parts.length === 2) {
      const [endHour, endMinute] = parts.map(Number);
      if (!isNaN(endHour) && !isNaN(endMinute)) {
        endBookingDate
          .tz('Europe/London')
          .hours(endHour)
          .minutes(endMinute)
          .seconds(0)
          .milliseconds(0);
      }
    }
  }

  return endBookingDate;
};

export default calculateEndBookingDate;
